import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "theme-ui";
import { deleteDepot, getDepot, updateDepot } from "api/Providers/Depots";
import SearchField from "components/forms/SearchField";
import { filterByKeys } from "helpers/default";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon, QuestionMarkIcon, TrashIcon, PlusIcon, ProductsIcon, ServicesIcon, PencilIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import GooglePlaces from "components/forms/GooglePlaces";
import Checkbox from "components/forms/Checkbox";
import SelectSearch from "components/forms/SelectSearch";
import { getServiceAreas } from "api/ServiceAreas";
import EditModal from "containers/ServiceArea/EditModal";
import CreateModal from "containers/ServiceArea/CreateModal";
import DrawGoogleMap from "components/forms/DrawGoogleMap";
import SelectInput from "components/forms/SelectInput";
import { getProducts } from "api/Products";
import { getServices } from "api/Services";
import { createDepotComboProduct, getDepotComboProducts, updateDepotProductCombo, deleteDepotComboProduct } from "api/Providers/Depots";
import { getInstantEstimateCombos } from "api/Companies";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

interface ProductModalValues {
  depotProducts: number[];
}

interface ServiceModalValues {
  depotServices: number[];
}

export default function Depot() {
  const params = useParams();
  const toast = useToast();

  const [depot, setDepot] = useState<any>(null);
  const [productOpen, setProductOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [productComboOpen, setProductComboOpen] = useState(false);
  const [comboEditOpen, setComboEditOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [depotProducts, setDepotProducts] = useState<any>([]);
  const [depotServices, setDepotServices] = useState<any>([]);
  const [selectedCombo, setSelectedCombo] = useState<any>(null);
  const [bulkProduct, setBulkProduct] = useState<string | null>(null);
  const [localEstimateItems, setLocalEstimateItems] = useState<any[]>([]);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [servicesList, setServicesList] = useState<any[]>([]);
  const [productCombos, setProductCombos] = useState<any[]>([]);
  const [editCombo, setEditCombo] = useState<any>(null);
  const [canAddInstantEstimate, setCanAddInstantEstimate] = useState(true);
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  //placeholder for declaring there is a product combo setup
  const hasProdCombo = true;

  const submitUpdate = async (values: any) => {
    const keys = [
      "name",
      "contactEmail",
      "address.line1",
      "address.line2",
      "address.city",
      "address.state",
      "address.postalCode",
      "address.country",
      "latitude",
      "longitude",
      "preferred",
      "serviceAreaId",
      "instant_estimate_items",
    ];

    const update = filterByKeys(keys, values);

    if (values.depotProducts) {
      update.products = values.depotProducts;
    }
    if (values.depotServices) {
      update.services = values.depotServices;
    }

    const res = await updateDepot({ provider: { id: params.providerId }, data: depot, update });
    if (res?.id) {
      setDepot(res);
      toast.add("Depot updated.", "var(--theme-ui-colors-success)");

      const changedFields: string[] = [];

      const isEqual = (a: any, b: any) => {
        if (a === b) return true;
        if (typeof a === "undefined" || a === null) {
          return typeof b === "undefined" || b === null;
        }
        if (typeof b === "undefined" || b === null) {
          return false;
        }
        try {
          return JSON.stringify(a) === JSON.stringify(b);
        } catch {
          return false;
        }
      };

      if (!isEqual(depot?.name, res?.name)) {
        changedFields.push("name");
      }

      if (!isEqual(depot?.contactEmail, res?.contactEmail)) {
        changedFields.push("contactEmail");
      }

      const prevAddress = depot?.address || {};
      const nextAddress = res?.address || {};
      const addressKeys = ["line1", "line2", "city", "state", "postalCode", "country"];
      addressKeys.forEach((key) => {
        if (!isEqual(prevAddress?.[key], nextAddress?.[key])) {
          changedFields.push(`address.${key}`);
        }
      });

      if (!isEqual(depot?.latitude, res?.latitude)) {
        changedFields.push("latitude");
      }

      if (!isEqual(depot?.longitude, res?.longitude)) {
        changedFields.push("longitude");
      }

      if (!isEqual(depot?.preferred, res?.preferred)) {
        changedFields.push("preferred");
      }

      const prevServiceAreaId = depot?.serviceArea?.id || null;
      const nextServiceAreaId = res?.serviceArea?.id || null;
      if (!isEqual(prevServiceAreaId, nextServiceAreaId)) {
        changedFields.push("serviceAreaId");
      }

      const prevProducts = Array.isArray(depot?.products) ? depot.products.map((p: any) => p.id).sort() : [];
      const nextProducts = Array.isArray(res?.products) ? res.products.map((p: any) => p.id).sort() : [];
      if (!isEqual(prevProducts, nextProducts)) {
        changedFields.push("products");
      }

      const prevServices = Array.isArray(depot?.services) ? depot.services.map((s: any) => s.id).sort() : [];
      const nextServices = Array.isArray(res?.services) ? res.services.map((s: any) => s.id).sort() : [];
      if (!isEqual(prevServices, nextServices)) {
        changedFields.push("services");
      }

      const prevEstimates = Array.isArray(depot?.instant_estimate_items) ? depot.instant_estimate_items : [];
      const nextEstimates = Array.isArray(res?.instant_estimate_items) ? res.instant_estimate_items : [];
      if (!isEqual(prevEstimates, nextEstimates)) {
        changedFields.push("instant_estimate_items");
      }

      if (changedFields.length > 0) {
        void trackActivity({
          module: "providers",
          action: "update_depot",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "depot",
          entityId: res?.id ?? depot?.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating depot", "var(--theme-ui-colors-danger)");
    }
  };

  const _validate = Yup.object().shape({
    contactEmail: Yup.string().email("Invalid email").required("Email is required."),
    name: Yup.string().required("Depot name is required."),
  });

  const _prodValidate = Yup.object().shape({
    selectedProducts: Yup.array().min(1, "Please select at least one product").of(Yup.number()),
  });

  const _getIETemplates = async () => {
    const res = await getInstantEstimateCombos();
    if (res.items && res.items.length > 0) {
      const transformedCombos = res.items.map((item: any) => ({
        id: item.id,
        bulk_product_id: item.product.id,
        related_services: item.services,
        add_on_products: item.addons,
      }));

      setProductCombos(transformedCombos);
    }
  };

  const _getDepotCombos = async () => {
    const res = await getDepotComboProducts({ data: { providerId: params.providerId, depotId: params.id } });

    if (res.items && res.items.length > 0) {
      const transformedItems = res.items.map((item: any) => ({
        bulk_product_name: item.product.name,
        bulk_product_id: item.product.id,
        id: item.id,
        related_services: item.services.map((service: any) => ({
          service_id: service.serviceId,
          name: service.name,
          low_cost: service.lowCost,
          high_cost: service.highCost,
          markup: service.markup,
          id: service.id,
        })),
        add_ons: item.addons.map((addOn: any) => ({
          product_id: addOn.addonId,
          name: addOn.name,
          base_cost: addOn.baseCost,
          markup: addOn.markup,
          id: addOn.id,
        })),
      }));

      setLocalEstimateItems(transformedItems); // Directly set new data
    }
  };

  const initDepot = async () => {
    const res = await getDepot({ provider: { id: params.providerId }, data: { id: params.id } });
    const prodRes = await getProducts({});
    const servRes = await getServices({});
    _getIETemplates();
    setServicesList(servRes.items);
    setProductsList(prodRes.items);
    setDepot(res?.id ? res : null);
    setDepotProducts(res?.products || []);
    setDepotServices(res?.services || []);
    _getDepotCombos();
  };

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const confirmDelete = async () => {
    setConfirmSubmit(true);
    const res: any = await deleteDepot({ provider: { id: params.providerId }, data: depot });
    if (res?.success) {
      toast.add("Depot deactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "providers",
        action: "deactivate_depot",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "depot",
        entityId: depot?.id ?? null,
        metadata: {
          source: "depot_edit",
          providerId: params.providerId ?? null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } else {
      toast.add(res?.message ? res.message : "Error deleting depot.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate depot?"
        size={"sm"}
        cancel_text="No, cancel"
        cancel_click={() => setConfirmModal(false)}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton type="button" scheme="danger" onClick={() => confirmDelete()} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
            {!confirmSubmit && <TrashIcon fill={"#fff"} />}
            {"Deactivate"}
          </BasicButton>
        }
      >
        <P>
          Are you sure you want to deactivate depot <strong style={{ color: "var(--theme-ui-colors-black)" }}>{depot?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  const toggleProductModal = () => {
    setProductOpen(!productOpen);
    setSearchTerm("");
  };

  const toggleServiceModal = () => {
    setServiceOpen(!serviceOpen);
    setSearchTerm("");
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Depot";
    initDepot();
  }, []);

  useEffect(() => {
    if (!depot?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "providers",
      action: "view_depot",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "depot",
      entityId: depot.id,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [depot?.id, user?.id, user?.company?.id]);

  if (!depot || productsList.length === 0 || servicesList.length === 0)
    return (
      <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
        <Spinner size={25} strokeWidth={3} title="loading" />
      </Box>
    );

  return (
    <>
      <Header />
      <Page
        title="Edit depot"
        subtitle="Edit your provider with updated details."
        backAction={
          <Link variant="back" href={`/providers/${params.providerId}`}>
            <BackArrowIcon />
            Back to Provider
          </Link>
        }
      >
        <Formik
          initialValues={{
            ...depot,
            serviceAreaId: depot?.serviceArea?.id,
            depotProducts: depot?.products || [],
            depotServices: depot?.services || [],
            instant_estimate_items: localEstimateItems,
            googlePlace: [
              depot?.address?.line1,
              depot?.address?.line2,
              [depot?.address?.city, [depot?.address?.state, depot?.address?.postalCode].filter(Boolean).join(" "), depot?.address?.country].filter(Boolean).join(", "),
            ]
              .filter(Boolean)
              .join(", "),
          }}
          onSubmit={submitUpdate}
          validationSchema={_validate}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue, submitCount, errors, submitForm }) => {
            const filteredProducts = productsList.filter((product) => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
            const filteredServices = servicesList.filter((service) => service.name.toLowerCase().includes(searchTerm.toLowerCase()));

            const submitProducts = (selectedProductIds: number[]) => {
              const newProducts = productsList.filter((p) => selectedProductIds.includes(p.id));

              // Ensure no duplicate IDs
              const uniqueProducts = [...new Map([...values.depotProducts, ...newProducts].map((p) => [p.id, p])).values()];

              setFieldValue("depotProducts", uniqueProducts);
              setDepotProducts(uniqueProducts);
              setProductOpen(false);
              submitForm();
            };

            useEffect(() => {
              if (productCombos.length === 0) {
                setCanAddInstantEstimate(false);
                return;
              }

              if (values.instant_estimate_items.length == 0 && productCombos.length > 0) {
                setCanAddInstantEstimate(true);
                return;
              }

              const allCombosRepresented = productCombos.every((combo) => values.instant_estimate_items.some((item: any) => item.bulk_product_id === combo.bulk_product_id));

              setCanAddInstantEstimate(!allCombosRepresented);
            }, [productCombos, values.instant_estimate_items]);

            const submitServices = (selectedServiceIds: number[]) => {
              const newServices = servicesList.filter((s) => selectedServiceIds.includes(s.id));

              // Ensure no duplicate IDs
              const uniqueServices = [...new Map([...values.depotServices, ...newServices].map((s) => [s.id, s])).values()];

              setFieldValue("depotServices", uniqueServices);
              setDepotServices(uniqueServices);
              setServiceOpen(false);
              submitForm();
            };

            const removeProduct = (productId: number) => {
              const isUsedInEstimates = values.instant_estimate_items.some((item: any) => item.bulk_product_id === productId || item.add_ons.some((addOn: any) => addOn.product_id === productId));

              if (isUsedInEstimates) {
                toast.add("This product is used in an Instant Estimate item and cannot be removed.", "var(--theme-ui-colors-danger)");
                return;
              }

              const updatedProducts = depotProducts.filter((p: any) => p.id !== productId);
              setFieldValue("depotProducts", updatedProducts);
              setDepotProducts(updatedProducts);
              submitForm();
            };

            const removeService = (serviceId: number) => {
              const isUsedInEstimates = values.instant_estimate_items.some((item: any) => item.related_services.some((service: any) => service.service_id === serviceId));

              if (isUsedInEstimates) {
                toast.add("This service is used in an Instant Estimate item and cannot be removed.", "var(--theme-ui-colors-danger)");
                return;
              }

              const updatedServices = depotServices.filter((s: any) => s.id !== serviceId);
              setFieldValue("depotServices", updatedServices);
              setDepotServices(updatedServices);
              submitForm();
            };

            const submitProductCombo = async (values: any) => {
              if (!selectedCombo) return;

              const transformedItem = {
                bulk_product_name: productsList.find((p) => p.id === selectedCombo.bulk_product_id)?.name || "Unknown",
                bulk_product_id: selectedCombo.bulk_product_id,
                id: selectedCombo.id,
                related_services:
                  values.services?.map((service: any, idx: number) => ({
                    service_id: selectedCombo.related_services[idx].id,
                    name: selectedCombo.related_services[idx].name,
                    low_cost: service.low_cost || 0,
                    high_cost: service.high_cost || 0,
                    markup: service.markup || 0,
                    id: service.id,
                  })) || [],
                add_ons:
                  values.addOns?.map((addOn: any, idx: number) => ({
                    product_id: selectedCombo.add_on_products[idx].id,
                    name: selectedCombo.add_on_products[idx].name,
                    base_cost: addOn.base_cost || 0,
                    markup: addOn.add_on_markup || 0,
                    id: addOn.id,
                  })) || [],
              };

              const matchedItem = productCombos.find((item) => item.bulk_product_id === selectedCombo.bulk_product_id);

              const submissionItem = {
                templateProductId: matchedItem ? matchedItem.id : null,
                services:
                  values.services?.map((service: any, idx: number) => ({
                    serviceId: selectedCombo.related_services[idx].serviceId,
                    lowCost: service.low_cost || 0,
                    highCost: service.high_cost || 0,
                    markup: service.markup || 0,
                  })) || [],
                addons:
                  values.addOns?.map((addOn: any, idx: number) => ({
                    addonId: selectedCombo.add_on_products[idx].addonId,
                    baseCost: addOn.base_cost || 0,
                    markup: addOn.add_on_markup || 0,
                  })) || [],
              };

              const res = await createDepotComboProduct({ data: { providerId: params.providerId, depotId: params.id }, create: submissionItem });

              if (res?.id) {
                toast.add("Instant Estimate item added.", "var(--theme-ui-colors-success)");
                setFieldValue("instant_estimate_items", [...(values.instant_estimate_items || []), transformedItem]);
                initDepot();
              } else {
                toast.add("Error adding Instant Estimate item.", "var(--theme-ui-colors-danger)");
              }

              setProductComboOpen(false);
            };

            const submitRemove = async (comboId: number) => {
              setConfirmModal(
                <BasicModal
                  title="Remove item?"
                  size={"sm"}
                  cancel_text="No, cancel"
                  cancel_click={() => setConfirmModal(false)}
                  icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
                  actions={
                    <BasicButton
                      type="button"
                      scheme="danger"
                      onClick={() => removeProductCombo(comboId)}
                      $submitting={confirmSubmit}
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor="#fff"
                      keepText={true}
                    >
                      {!confirmSubmit && <TrashIcon fill={"#fff"} />}
                      {"Remove"}
                    </BasicButton>
                  }
                >
                  <P lineHeight={"22px"}>Are you sure you want to remove this Instant Estimate Item?</P>
                </BasicModal>
              );
            };

            const openEdit = (index: number) => {
              setEditCombo(values.instant_estimate_items[index]);
              setComboEditOpen(true);
            };

            useEffect(() => {
              if (editCombo) {
                setComboEditOpen(true);
              }
            }, [editCombo]);

            const submitEdit = async (values: any) => {
              const data = {
                id: params.id,
                providerId: params.providerId,
                itemId: values.id,
              };
              const update = {
                services: values.services.map((service: any, idx: number) => ({
                  serviceId: service.service_id,
                  lowCost: Number(parseFloat(service.low_cost).toFixed(2)) || 0,
                  highCost: Number(parseFloat(service.high_cost).toFixed(2)) || 0,
                  markup: Number(parseFloat(service.markup).toFixed(2)) || 0,
                  id: service.id,
                })),
                addons: values.addons.map((addOn: any, idx: number) => ({
                  addonId: addOn.addon_id,
                  baseCost: Number(parseFloat(addOn.base_cost).toFixed(2)) || 0,
                  markup: Number(parseFloat(addOn.markup).toFixed(2)) || 0,
                  id: addOn.id,
                })),
              };

              const res = await updateDepotProductCombo({ data, update });
              if (res?.id) {
                toast.add("Instant Estimate item updated.", "var(--theme-ui-colors-success)");
                setEditCombo(null);
                setComboEditOpen(false);
                initDepot();
              } else {
                toast.add("Error updating Instant Estimate item.", "var(--theme-ui-colors-danger)");
              }
            };

            const removeProductCombo = async (index: number) => {
              const data = {
                id: params.id,
                providerId: params.providerId,
                itemId: values.instant_estimate_items[index].id,
              };
              const res = await deleteDepotComboProduct({ data });

              if (res?.success) {
                toast.add("Instant Estimate item removed.", "var(--theme-ui-colors-success)");
                const updatedItems = values.instant_estimate_items.filter((item: any, idx: number) => idx !== index);
                setFieldValue("instant_estimate_items", updatedItems);
                setLocalEstimateItems(updatedItems);
                setConfirmModal(false);
                initDepot();
              } else {
                toast.add("Error removing Instant Estimate item.", "var(--theme-ui-colors-danger)");
              }
            };

            const toggleComboModal = () => {
              setProductComboOpen(!productComboOpen);
              if (!productComboOpen) {
                resetComboModal();
              }
            };

            const toggleComboEditModal = () => {
              if (comboEditOpen) {
                setEditCombo(null);
              }
              setComboEditOpen(!comboEditOpen);
            };

            const getAvailableCombos = () => {
              return productCombos.filter((combo) => depotProducts.some((p: any) => p.id === combo.bulk_product_id));
            };

            const handleBulkProductChange = (productId: string) => {
              const combo = productCombos.find((c) => c.bulk_product_id === productId);
              setSelectedCombo(combo || null);
              setBulkProduct(productId);
            };

            const availableCombos = getAvailableCombos();

            const bulkProductOptions = availableCombos
              .filter((combo) => !values.instant_estimate_items.some((item: any) => item.bulk_product_id === combo.bulk_product_id)) // Exclude already selected bulk products
              .map((combo) => ({
                label: productsList.find((p) => p.id === combo.bulk_product_id)?.name || "",
                value: combo.bulk_product_id,
              }));

            const resetComboModal = () => {
              const availableOptions = availableCombos.filter((combo) => !values.instant_estimate_items.some((item: any) => item.bulk_product_id === combo.bulk_product_id));

              if (availableOptions.length > 0) {
                const firstCombo = availableOptions[0];
                setBulkProduct(firstCombo.bulk_product_id);
                setSelectedCombo(firstCombo);
              } else {
                setBulkProduct(null);
                setSelectedCombo(null);
              }
            };

            return (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1 }}>
                      <BasicButton scheme="dangerSecondary" styles={{ marginRight: "auto!important", marginLeft: "10px" }} type="button" onClick={() => submitDelete()}>
                        <TrashIcon fill={themevals.colors.danger} /> Deactivate
                      </BasicButton>
                      <Link href="/providers" sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Depot
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit Depot basic information.
                        </Subtext>
                      </Box>
                      <Box>
                        <TextInput
                          name="name"
                          label="Depot Name"
                          value={values.name}
                          onChange={(val: any) => setFieldValue("name", val)}
                          $errors={errors?.name && submitCount > 0 ? errors?.name : null}
                        />
                        <Checkbox label="Preferred" checked={values.preferred} onChange={(val: any) => setFieldValue("preferred", !values.preferred)} />
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]}sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Address
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit Depot Address.
                        </Subtext>
                      </Box>
                      <Box>
                        <GooglePlaces
                          label="Address"
                          value={values.googlePlace}
                          onChange={(val: any) => {
                            setFieldValue("address", val?.address);
                            setFieldValue("latitude", val?.latitude);
                            setFieldValue("longitude", val?.longitude);
                          }}
                          $errors={(errors?.address || errors?.latitude || errors?.longitude) && submitCount > 0 ? errors?.address ?? errors?.latitude ?? errors?.longitude : null}
                        />
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Email
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit Depot contact email.
                        </Subtext>
                      </Box>
                      <Box>
                        <TextInput
                          type="email"
                          name="contactEmail"
                          label="Contact Email"
                          value={values.contactEmail}
                          onChange={(val: any) => setFieldValue("contactEmail", val)}
                          $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}
                        />
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Depot Products
                        </H5>
                        <Subtext marginTop={0} marginBottom={30} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit products associated with this depot.
                        </Subtext>
                        <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" onClick={() => toggleProductModal()}>
                          <PlusIcon fill="#000" />
                          Add product
                        </BasicButton>
                      </Box>
                      <Box>
                        <Grid gap="20px" columns={[2, "1fr 1fr"]}>
                          {values.depotProducts.length > 0 ? (
                            values.depotProducts.map((product: any) => (
                              <Flex
                                key={product.id}
                                sx={{
                                  border: `1px solid ${themevals.colors.mdgrey}`,
                                  borderRadius: "6px",
                                  height: "38px",
                                  padding: "0px 15px",
                                  display: "flex",
                                  alignItems: "center",
                                  fontFamily: "Poppins",
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  color: themevals.colors.black,
                                  lineHeight: "14px",
                                }}
                              >
                                <Flex sx={{ flex: "1" }}>
                                  <P marginBottom="0px" marginTop="0px">{product.name}</P>
                                </Flex>
                                <Flex sx={{ flex: "1", justifyContent: "flex-end" }}>
                                  <Box sx={{ cursor: "pointer" }} onClick={() => removeProduct(product.id)}>
                                    <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                  </Box>
                                </Flex>
                              </Flex>
                            ))
                          ) : (
                            <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                              There are no products tied to this depot currently.
                            </P>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Depot Services
                        </H5>
                        <Subtext marginTop={0} marginBottom={30} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit services associated with this depot.
                        </Subtext>
                        <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" onClick={() => toggleServiceModal()}>
                          <PlusIcon fill="#000" />
                          Add service
                        </BasicButton>
                      </Box>
                      <Box>
                        <Grid gap="20px" columns={[2, "1fr 1fr"]}>
                          {values.depotServices.length > 0 ? (
                            values.depotServices.map((service: any) => (
                              <Flex
                                key={service.id}
                                sx={{
                                  border: `1px solid ${themevals.colors.mdgrey}`,
                                  borderRadius: "6px",
                                  height: "38px",
                                  padding: "0px 15px",
                                  display: "flex",
                                  alignItems: "center",
                                  fontFamily: "Poppins",
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  color: themevals.colors.black,
                                  lineHeight: "14px",
                                }}
                              >
                                <Flex sx={{ flex: "1" }}>
                                  <P marginBottom="0px" marginTop="0px">{service.name}</P>
                                </Flex>
                                <Flex sx={{ flex: "1", justifyContent: "flex-end" }}>
                                  <Box sx={{ cursor: "pointer" }} onClick={() => removeService(service.id)}>
                                    <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                  </Box>
                                </Flex>
                              </Flex>
                            ))
                          ) : (
                            <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                              There are no services tied to this depot currently.
                            </P>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Service Area
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the service area associated with this depot.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "1rem", alignItems: "center" }}>
                          <SelectSearch
                            api={getServiceAreas}
                            multiple={false}
                            placeholder="None"
                            name="serviceAreaId"
                            label="Service Area"
                            value={values?.serviceAreaId}
                            keyLabel={["name"]}
                            keyValue="id"
                            preload
                            onChange={(val: any) => setFieldValue("serviceAreaId", val?.id)}
                            $errors={errors?.serviceAreaId && submitCount > 0 ? errors?.serviceAreaId : null}
                            $customStyles={{ width: 400 }}
                            limit={0}
                          />
                          {depot.serviceArea?.id && <EditModal includeButton isOpen={false} serviceArea={depot?.serviceArea} afterUpdate={() => initDepot()} />}
                          <CreateModal includeButton isOpen={false} bottomFix={true} afterCreate={(val) => setFieldValue("serviceAreaId", val?.id)} />
                        </Flex>
                        {depot.serviceArea && values?.serviceAreaId && <DrawGoogleMap value={depot?.serviceArea?.geometry} disabled />}
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Instant Estimate Items
                        </H5>
                        <Subtext marginTop={0} marginBottom={30} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Edit the items available in the Instant Estimate tool.
                        </Subtext>
                        {canAddInstantEstimate && depotServices.length > 0 && depotProducts.length > 0 && (
                          <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" onClick={() => toggleComboModal()}>
                            <PlusIcon fill="#000" />
                            Add item
                          </BasicButton>
                        )}
                      </Box>
                      <Box>
                        {values.instant_estimate_items &&
                          values.instant_estimate_items.length > 0 &&
                          values.instant_estimate_items.map((item: any, index: number) => (
                            <Flex key={index} sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", marginBottom: "10px", flexDirection: "column" }}>
                              <Box sx={{ borderBottom: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", display: "flex" }}>
                                <Flex sx={{ flex: 1, alignItems: "center" }}>
                                  <H5 marginTop="0px" marginBottom="0px" fontSize="14px" fontFamily="Poppins" fontWeight="600">
                                    {item.bulk_product_name} <span style={{ color: themevals.colors.dkgrey, fontSize: 12, fontWeight: "400" }}>- Bulk Product</span>
                                  </H5>
                                </Flex>
                                <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                  <BasicButton scheme="secondaryOutline" type="button" onClick={() => openEdit(index)} styles={{ marginRight: "20px" }}>
                                    <PencilIcon fill={themevals.colors.black} /> Edit
                                  </BasicButton>
                                  <BasicButton scheme="dangerSecondary" type="button" onClick={() => submitRemove(index)}>
                                    <TrashIcon fill={themevals.colors.danger} /> Remove
                                  </BasicButton>
                                </Flex>
                              </Box>
                              {item.related_services.length > 0 && (
                                <Box sx={{ padding: "20px" }}>
                                  <H5 marginTop="0px" marginBottom="20px" fontSize="12px" fontFamily="Poppins" color={"#000"}>
                                    Related Services
                                  </H5>
                                  {item.related_services.map((service: any, idx: number) => (
                                    <Flex key={idx} sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", marginBottom: "10px", padding: "20px", alignItems: "center" }}>
                                      <Flex sx={{ flex: 1 }}>
                                        <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px">
                                          {service.name}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                                          Low Cost
                                        </Subtext>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                                          ${Number(service.low_cost).toFixed(2)}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                                          High Cost
                                        </Subtext>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                                          ${Number(service.high_cost).toFixed(2)}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                                          Markup
                                        </Subtext>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                                          {service.markup}%
                                        </P>
                                      </Flex>
                                    </Flex>
                                  ))}
                                </Box>
                              )}
                              {item.add_ons.length > 0 && (
                                <Box sx={{ padding: "20px" }}>
                                  <H5 marginTop="0px" marginBottom="20px" fontSize="12px" fontFamily="Poppins" color={"#000"}>
                                    Add-on Products
                                  </H5>
                                  {item.add_ons.map((addOn: any, idx: number) => (
                                    <Flex key={idx} sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", marginBottom: "10px", padding: "20px", alignItems: "center" }}>
                                      <Flex sx={{ flex: 1 }}>
                                        <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px">
                                          {addOn.name}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                                          Base Cost
                                        </Subtext>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                                          ${addOn.base_cost}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                                          Markup
                                        </Subtext>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                                          {addOn.markup}%
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "column", flex: 1 }}></Flex>
                                    </Flex>
                                  ))}
                                </Box>
                              )}
                            </Flex>
                          ))}

                        {/* {has combo setup and product and services} */}
                        {canAddInstantEstimate && depotServices.length > 0 && depotProducts.length > 0 && values.instant_estimate_items.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            There are currently no Instant Estimate items associated with this depot.
                          </P>
                        )}
                        {/* {has combo and services but no products} */}
                        {canAddInstantEstimate && depotServices.length > 0 && depotProducts.length == 0 && values.instant_estimate_items.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item you must first have products that match the product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            , attached to this depot.
                          </P>
                        )}
                        {/* {has combo and products but no services} */}
                        {canAddInstantEstimate && depotServices.length == 0 && depotProducts.length > 0 && values.instant_estimate_items.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item you must first have services that match the product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            , attached to this depot.
                          </P>
                        )}
                        {/* {does not have combo setup and no products and no services} */}
                        {!canAddInstantEstimate && depotServices.length == 0 && depotProducts.length == 0 && values.instant_estimate_items.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item, you must first add a product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            and attach products and services to this depot to match that combination.
                          </P>
                        )}
                        {/* {No Combo and no services but has products} */}
                        {!canAddInstantEstimate && depotServices.length == 0 && depotProducts.length > 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item, you must first add a product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            and attach services to this depot to match that combination.
                          </P>
                        )}
                        {/* {no combo and no products but has service} */}
                        {!canAddInstantEstimate && depotServices.length > 0 && depotProducts.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item, you must first add a product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            and attach products to this depot to match that combination.
                          </P>
                        )}
                        {/* {has combo setup and no products and no services} */}
                        {canAddInstantEstimate && depotServices.length == 0 && depotProducts.length == 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item you must first have products and services that match the product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>{" "}
                            , attached to this depot.
                          </P>
                        )}
                        {/* {does not have combo setup but has product and services} */}
                        {productCombos?.length == 0 && depotServices.length > 0 && depotProducts.length > 0 && (
                          <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey}>
                            To add an Instant Estimate item, you must first add a product combination in{" "}
                            <Link variant="forward" href={`/company?tab=instant_estimate`}>
                              settings
                            </Link>
                            .
                          </P>
                        )}
                      </Box>
                    </Grid>
                  </Tab>
                </Tabs>
                <Modal isOpen={productOpen} toggle={() => toggleProductModal()} centered size={"sm"}>
                  <Formik<ProductModalValues> initialValues={{ depotProducts: [] }} onSubmit={(values) => submitProducts(values.depotProducts)} enableReinitialize validationSchema={_prodValidate}>
                    {({
                      isSubmitting,
                      values,
                      setFieldValue,
                      submitCount,
                      errors,
                    }: {
                      isSubmitting: boolean;
                      values: any;
                      setFieldValue: (name: string, val?: any) => void;
                      submitCount: number;
                      errors: any;
                    }) => {
                      const toggleProduct = (productId: number) => {
                        const newSelected: number[] = values.depotProducts.includes(productId) ? values.depotProducts.filter((id: number) => id !== productId) : [...values.depotProducts, productId];
                        setFieldValue("depotProducts", newSelected);
                      };

                      const toggleSelectAll = () => {
                        if (values.depotProducts.length === filteredProducts.length) {
                          setFieldValue("depotProducts", []);
                        } else {
                          setFieldValue(
                            "depotProducts",
                            filteredProducts.map((p) => p.id)
                          );
                        }
                      };

                      const availableProductIds = depotProducts.map((p: any) => p.id);

                      const availableProducts = React.useMemo(() => {
                        return productsList.filter((product) => !availableProductIds.includes(product.id) && product.name.toLowerCase().includes(searchTerm.toLowerCase()));
                      }, [depotProducts, searchTerm]);

                      return (
                        <Form noValidate autoComplete="off">
                          <ModalHeader toggle={() => toggleProductModal()}>
                            <ModalIcon>
                              <ProductsIcon fill={"000"} />
                            </ModalIcon>
                            <ModalTitleWrapper>
                              <ModalTitle>Add products</ModalTitle>
                              <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                            </ModalTitleWrapper>
                          </ModalHeader>
                          <ModalBody>
                            <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                            <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                              <Checkbox label="Select All" checked={values.depotProducts.length === filteredProducts.length && filteredProducts.length > 0} onChange={toggleSelectAll} />
                            </Flex>
                            {availableProducts.map((product, idx) => (
                              <Flex
                                key={product.id}
                                sx={{
                                  gap: "1rem",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                  border: `1px solid ${values.depotProducts.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                                  borderRadius: "6px",
                                  height: "38px",
                                  padding: "0px 10px",
                                }}
                              >
                                <Checkbox label={product.name} checked={values.depotProducts.includes(product.id)} onChange={() => toggleProduct(product.id)} labelSize="14px" />
                              </Flex>
                            ))}
                          </ModalBody>
                          <ModalFooter>
                            <Button variant="secondaryOutline" type="button" onClick={() => setProductOpen(false)} style={{ marginRight: "10px" }}>
                              Cancel
                            </Button>
                            <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                              {!isSubmitting && <PlusIcon fill={"#fff"} />}
                              Add products
                            </BasicButton>
                          </ModalFooter>
                        </Form>
                      );
                    }}
                  </Formik>
                </Modal>
                <Modal isOpen={serviceOpen} toggle={() => toggleServiceModal()} centered size={"sm"}>
                  <Formik<ServiceModalValues> initialValues={{ depotServices: [] }} onSubmit={(values) => submitServices(values.depotServices)} enableReinitialize validationSchema={_prodValidate}>
                    {({ isSubmitting, values, setFieldValue, errors }: { isSubmitting: boolean; values: any; setFieldValue: (name: string, val?: any) => void; errors: any }) => {
                      const toggleService = (serviceId: number) => {
                        const newSelected: number[] = values.depotServices.includes(serviceId) ? values.depotServices.filter((id: number) => id !== serviceId) : [...values.depotServices, serviceId];
                        setFieldValue("depotServices", newSelected);
                      };

                      const toggleSelectAll = () => {
                        if (values.depotServices.length === filteredServices.length) {
                          setFieldValue("depotServices", []);
                        } else {
                          setFieldValue(
                            "depotServices",
                            filteredServices.map((p) => p.id)
                          );
                        }
                      };

                      const availableServiceIds = depotServices.map((p: any) => p.id);

                      const availableServices = React.useMemo(() => {
                        return servicesList.filter((service) => !availableServiceIds.includes(service.id) && service.name.toLowerCase().includes(searchTerm.toLowerCase()));
                      }, [depotServices, searchTerm]);
                      return (
                        <Form noValidate autoComplete="off">
                          <ModalHeader toggle={() => toggleServiceModal()}>
                            <ModalIcon>
                              <ServicesIcon fill={"000"} />
                            </ModalIcon>
                            <ModalTitleWrapper>
                              <ModalTitle>Add services</ModalTitle>
                              <ModalSubtitle>{"Add available services at this depot."}</ModalSubtitle>
                            </ModalTitleWrapper>
                          </ModalHeader>
                          <ModalBody>
                            <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                            <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                              <Checkbox label="Select All" checked={values.depotServices.length === filteredServices.length && filteredServices.length > 0} onChange={toggleSelectAll} />
                            </Flex>
                            {availableServices.map((service, idx) => (
                              <Flex
                                key={service.id}
                                sx={{
                                  gap: "1rem",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                  border: `1px solid ${values.depotServices.includes(service.id) ? "black" : themevals.colors.mdgrey}`,
                                  borderRadius: "6px",
                                  height: "38px",
                                  padding: "0px 10px",
                                }}
                              >
                                <Checkbox label={service.name} checked={values.depotServices.includes(service.id)} onChange={() => toggleService(service.id)} labelSize="14px" />
                              </Flex>
                            ))}
                          </ModalBody>
                          <ModalFooter>
                            <Button variant="secondaryOutline" type="button" onClick={() => setServiceOpen(false)} style={{ marginRight: "10px" }}>
                              Cancel
                            </Button>
                            <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                              {!isSubmitting && <PlusIcon fill={"#fff"} />}
                              Add services
                            </BasicButton>
                          </ModalFooter>
                        </Form>
                      );
                    }}
                  </Formik>
                </Modal>
                <Modal isOpen={productComboOpen} toggle={() => toggleComboModal()} centered size={"sm"}>
                  <Formik initialValues={{}} onSubmit={(values) => submitProductCombo(values)} enableReinitialize>
                    {({ isSubmitting, values, setFieldValue, errors }: { isSubmitting: boolean; values: any; setFieldValue: (name: string, val?: any) => void; errors: any }) => {
                      const filteredServices = selectedCombo ? selectedCombo.related_services.filter((service: any) => depotServices.some((s: any) => s.id === service.serviceId)) : [];

                      const filteredAddOnProducts = selectedCombo ? selectedCombo.add_on_products.filter((addOn: any) => depotProducts.some((p: any) => p.id === addOn.addonId)) : [];

                      return (
                        <Form noValidate autoComplete="off">
                          <ModalHeader toggle={() => toggleComboModal()}>
                            <ModalIcon>
                              <ProductsIcon fill={"000"} />
                            </ModalIcon>
                            <ModalTitleWrapper>
                              <ModalTitle>Add Instant Estimate item</ModalTitle>
                              <ModalSubtitle>{"Add an item to a depot to be included in the Instant Estimate Tool."}</ModalSubtitle>
                            </ModalTitleWrapper>
                          </ModalHeader>
                          <ModalBody>
                            <Subtext fontSize="14px" fontFamily="Poppins" color="#000" fontWeight="600" marginTop="0px">
                              Primary Product
                            </Subtext>
                            <SelectInput
                              name="bulk_product"
                              label="Bulk Product"
                              value={bulkProduct !== null ? bulkProduct : ""}
                              options={bulkProductOptions}
                              onChange={(val) => handleBulkProductChange(val)}
                              $errors={errors.bulk_product && submitCount > 0 ? errors.bulk_product : null}
                              $customStyles={{ flexGrow: 1, width: "100%" }}
                            />
                            {filteredServices.length > 0 && (
                              <>
                                <Subtext fontSize="14px" fontFamily="Poppins" color="#000" fontWeight="600" marginTop="30px">
                                  Related Services
                                </Subtext>
                                {filteredServices.map((service: any, idx: number) => (
                                  <Flex
                                    key={service.id}
                                    sx={{
                                      border: `1px solid ${themevals.colors.mdgrey}`,
                                      borderRadius: "6px",
                                      padding: "0px 15px",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      fontFamily: "Poppins",
                                      fontSize: "14px",
                                      fontWeight: "400",
                                      color: themevals.colors.black,
                                      lineHeight: "14px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <H5 fontSize="14px" fontFamily="Poppins">
                                      {service.name}
                                    </H5>
                                    <Flex sx={{ width: "100%", justifyContent: "space-between" }}>
                                      <Grid columns={[3, "2fr 2fr 1fr"]} gap="10px" sx={{ width: "100%" }}>
                                        <Box>
                                          <TextInput
                                            name={`services[${idx}].low_cost`}
                                            label="Low Cost"
                                            type="number"
                                            value={values.services?.[idx]?.low_cost || ""}
                                            onChange={(val: any) => setFieldValue(`services[${idx}].low_cost`, val)}
                                            prefix={"$"}
                                            suffix={"/yrd."}
                                            $errors={errors?.services?.[idx]?.low_cost && submitCount > 0 ? errors.services[idx].low_cost : null}
                                          />
                                        </Box>
                                        <Box>
                                          <TextInput
                                            name={`services[${idx}].high_cost`}
                                            label="High Cost"
                                            type="number"
                                            value={values.services?.[idx]?.high_cost || ""}
                                            onChange={(val: any) => setFieldValue(`services[${idx}].high_cost`, val)}
                                            prefix={"$"}
                                            suffix={"/yrd."}
                                            $errors={errors?.services?.[idx]?.high_cost && submitCount > 0 ? errors.services[idx].high_cost : null}
                                          />
                                        </Box>
                                        <Box>
                                          <TextInput
                                            name={`services[${idx}].markup`}
                                            label="Markup"
                                            type="number"
                                            value={values.services?.[idx]?.markup || ""}
                                            onChange={(val: any) => setFieldValue(`services[${idx}].markup`, val)}
                                            suffix={"%"}
                                            $errors={errors?.markup && submitCount > 0 ? errors?.markup : null}
                                          />
                                        </Box>
                                      </Grid>
                                    </Flex>
                                  </Flex>
                                ))}
                              </>
                            )}
                            {filteredAddOnProducts.length > 0 && (
                              <>
                                <Subtext fontSize="14px" fontFamily="Poppins" color="#000" fontWeight="600" marginTop="30px">
                                  Add-on Products
                                </Subtext>
                                {filteredAddOnProducts.map((addOn: any, idx: any) => (
                                  <Flex
                                    key={addOn.id}
                                    sx={{
                                      border: `1px solid ${themevals.colors.mdgrey}`,
                                      borderRadius: "6px",
                                      padding: "0px 15px",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      fontFamily: "Poppins",
                                      fontSize: "14px",
                                      fontWeight: "400",
                                      color: themevals.colors.black,
                                      lineHeight: "14px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <H5 fontSize="14px">{addOn.name}</H5>
                                    <Flex sx={{ width: "100%", justifyContent: "space-between" }}>
                                      <Grid columns={[2, "4fr 1fr"]} gap="10px" sx={{ width: "100%" }}>
                                        <Box>
                                          <TextInput
                                            name={`addOns[${idx}].base_cost`}
                                            label="Base Cost"
                                            type="number"
                                            value={values.addOns?.[idx]?.base_cost || ""}
                                            onChange={(val: any) => setFieldValue(`addOns[${idx}].base_cost`, val)}
                                            prefix={"$"}
                                            suffix={"/sqft."}
                                            $errors={errors?.addOns?.[idx]?.base_cost && submitCount > 0 ? errors.addOns[idx].base_cost : null}
                                          />
                                        </Box>
                                        <Box>
                                          <TextInput
                                            name={`addOns[${idx}].add_on_markup`}
                                            label="Markup"
                                            type="number"
                                            value={values.addOns?.[idx]?.add_on_markup || ""}
                                            onChange={(val: any) => setFieldValue(`addOns[${idx}].add_on_markup`, val)}
                                            suffix={"%"}
                                            $errors={errors?.addOns?.[idx]?.add_on_markup && submitCount > 0 ? errors.addOns[idx].add_on_markup : null}
                                          />
                                        </Box>
                                      </Grid>
                                    </Flex>
                                  </Flex>
                                ))}
                              </>
                            )}
                          </ModalBody>
                          <ModalFooter>
                            <Button variant="secondaryOutline" type="button" onClick={() => setProductComboOpen(false)} style={{ marginRight: "10px" }}>
                              Cancel
                            </Button>
                            <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                              {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                              Save item
                            </BasicButton>
                          </ModalFooter>
                        </Form>
                      );
                    }}
                  </Formik>
                </Modal>
                <Modal isOpen={comboEditOpen} toggle={() => toggleComboEditModal()} centered size={"sm"}>
                  {editCombo && (
                    <Formik
                      initialValues={{
                        bulk_product_id: editCombo.bulk_product_id,
                        id: editCombo.id,
                        services: editCombo.related_services.map((service: any) => ({
                          service_id: service.service_id,
                          name: service.name,
                          low_cost: service.low_cost || "",
                          high_cost: service.high_cost || "",
                          markup: service.markup || "",
                          id: service.id,
                        })),
                        addons: editCombo.add_ons.map((addOn: any) => ({
                          addon_id: addOn.product_id,
                          name: addOn.name,
                          base_cost: addOn.base_cost || "",
                          markup: addOn.markup || "",
                          id: addOn.id,
                        })),
                      }}
                      onSubmit={(values) => submitEdit(values)}
                      enableReinitialize
                    >
                      {({ isSubmitting, values, setFieldValue, errors }: { isSubmitting: boolean; values: any; setFieldValue: (name: string, val?: any) => void; errors: any }) => {
                        const selectOptions = [{ value: editCombo.bulk_product_id, label: editCombo.bulk_product_name }];

                        return (
                          <Form noValidate autoComplete="off">
                            <ModalHeader toggle={() => toggleComboEditModal()}>
                              <ModalIcon>
                                <ProductsIcon fill={"000"} />
                              </ModalIcon>
                              <ModalTitleWrapper>
                                <ModalTitle>Edit Instant Estimate item</ModalTitle>
                                <ModalSubtitle>{"Edit your item for the Instant Estimate Tool."}</ModalSubtitle>
                              </ModalTitleWrapper>
                            </ModalHeader>
                            <ModalBody>
                              <SelectInput
                                name="bulk_product"
                                label="Bulk Product"
                                value={values.bulk_product_id}
                                options={selectOptions}
                                $errors={errors.bulk_product && submitCount > 0 ? errors.bulk_product : null}
                                $customStyles={{ flexGrow: 1, width: "100%" }}
                                disabled
                              />
                              {values.services.length > 0 && (
                                <>
                                  <Subtext fontSize="14px" fontFamily="Poppins" color="#000" fontWeight="600" marginTop="30px">
                                    Related Services
                                  </Subtext>
                                  {values.services.map((service: any, idx: number) => (
                                    <Flex
                                      key={service.id}
                                      sx={{
                                        border: `1px solid ${themevals.colors.mdgrey}`,
                                        borderRadius: "6px",
                                        padding: "0px 15px",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        fontFamily: "Poppins",
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: themevals.colors.black,
                                        lineHeight: "14px",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <H5 fontSize="14px" fontFamily="Poppins">
                                        {service.name}
                                      </H5>
                                      <Flex sx={{ width: "100%", justifyContent: "space-between" }}>
                                        <Grid columns={[3, "2fr 2fr 1fr"]} gap="10px" sx={{ width: "100%" }}>
                                          <Box>
                                            <TextInput
                                              name={`services[${idx}].low_cost`}
                                              label="Low Cost"
                                              type="number"
                                              value={values.services[idx].low_cost}
                                              onChange={(val: any) => setFieldValue(`services[${idx}].low_cost`, val)}
                                              prefix={"$"}
                                              suffix={"/yrd."}
                                              $errors={errors?.services?.[idx]?.low_cost}
                                            />
                                          </Box>
                                          <Box>
                                            <TextInput
                                              name={`services[${idx}].high_cost`}
                                              label="High Cost"
                                              type="number"
                                              value={values.services?.[idx]?.high_cost || ""}
                                              onChange={(val: any) => setFieldValue(`services[${idx}].high_cost`, val)}
                                              prefix={"$"}
                                              suffix={"/yrd."}
                                              $errors={errors?.services?.[idx]?.high_cost && submitCount > 0 ? errors.services[idx].high_cost : null}
                                            />
                                          </Box>
                                          <Box>
                                            <TextInput
                                              name={`services[${idx}].markup`}
                                              label="Markup"
                                              type="number"
                                              value={values.services[idx].markup ? parseInt(values.services[idx].markup, 10) : ""}
                                              onChange={(val: any) => setFieldValue(`services[${idx}].markup`, val ? parseInt(val, 10) : "")}
                                              suffix={"%"}
                                              $errors={errors?.services?.[idx]?.markup}
                                            />
                                          </Box>
                                        </Grid>
                                      </Flex>
                                    </Flex>
                                  ))}
                                </>
                              )}
                              {values.addons.length > 0 && (
                                <>
                                  <Subtext fontSize="14px" fontFamily="Poppins" color="#000" fontWeight="600" marginTop="30px">
                                    Add-on Products
                                  </Subtext>
                                  {values.addons.map((addOn: any, idx: any) => (
                                    <Flex
                                      key={addOn.id}
                                      sx={{
                                        border: `1px solid ${themevals.colors.mdgrey}`,
                                        borderRadius: "6px",
                                        padding: "0px 15px",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        fontFamily: "Poppins",
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        color: themevals.colors.black,
                                        lineHeight: "14px",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <H5 fontSize="14px">{addOn.name}</H5>
                                      <Flex sx={{ width: "100%", justifyContent: "space-between" }}>
                                        <Grid columns={[2, "4fr 1fr"]} gap="10px" sx={{ width: "100%" }}>
                                          <Box>
                                            <TextInput
                                              name={`addons[${idx}].base_cost`}
                                              label="Base Cost"
                                              type="number"
                                              value={values.addons?.[idx]?.base_cost || ""}
                                              onChange={(val: any) => setFieldValue(`addons[${idx}].base_cost`, val)}
                                              prefix={"$"}
                                              suffix={"/sqft."}
                                              $errors={errors?.addons?.[idx]?.base_cost && submitCount > 0 ? errors.addons[idx].base_cost : null}
                                            />
                                          </Box>
                                          <Box>
                                            <TextInput
                                              name={`addons[${idx}].markup`}
                                              label="Markup"
                                              type="number"
                                              value={values.addons?.[idx]?.markup !== undefined ? parseInt(values.addons[idx].markup, 10) : ""}
                                              onChange={(val: any) => setFieldValue(`addons[${idx}].markup`, val ? parseInt(val, 10) : "")}
                                              suffix={"%"}
                                              $errors={errors?.addons?.[idx]?.markup}
                                            />
                                          </Box>
                                        </Grid>
                                      </Flex>
                                    </Flex>
                                  ))}
                                </>
                              )}
                            </ModalBody>
                            <ModalFooter>
                              <Button variant="secondaryOutline" type="button" onClick={() => setComboEditOpen(false)} style={{ marginRight: "10px" }}>
                                Cancel
                              </Button>
                              <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                                {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                                Save item
                              </BasicButton>
                            </ModalFooter>
                          </Form>
                        );
                      }}
                    </Formik>
                  )}
                </Modal>
              </Form>
            );
          }}
        </Formik>
      </Page>
      {confirmModal}
    </>
  );
}
